import { API } from "./lib/api";
import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { CalendarThemeProvider } from "./components/calendar/CalendarTheme";
import ErrorBoundary from "./components/ErrorBoundary";
import LoginArrival from "./components/LoginArrival";
import ConnectionBanner from "./components/ConnectionBanner";
import { Toaster } from "./components/ui/sonner";
import { ConfirmProvider } from "./components/ConfirmProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import { clearSession, isSessionValid, isTabletSession } from "./lib/auth";
import { tryRefreshAccessToken } from "./lib/authRefresh";

// ResizeObserver loop uyarısını bastır.
// Bu, Radix Select / Dialog gibi bileşenlerin layout sırasında
// browser'in verdigi "benign" (zararsız) bir uyari olup uygulamayi etkilemez.
// React-error-overlay ve console'da gozukmesini engelleyelim.
if (typeof window !== "undefined") {
  const RO_MSG = "ResizeObserver loop";
  // Browser-level error events
  window.addEventListener("error", (e) => {
    if (e?.message && typeof e.message === "string" && e.message.includes(RO_MSG)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = e?.reason?.message || "";
    if (typeof msg === "string" && msg.includes(RO_MSG)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });
  // Console.error filtrele (CRA dev overlay bu mesajı buradan da yakalar)
  const _origConsoleError = console.error;
  console.error = (...args) => {
    const first = args[0];
    if (typeof first === "string" && first.includes(RO_MSG)) return;
    _origConsoleError.apply(console, args);
  };
}

// Code-splitting: Her panel ayrı chunk olarak yükleniyor → ilk yükleme küçük (Home + Auth UI),
// kullanıcı belirli paneli açtığında o chunk indirilir. Mobil 3G/4G performansı için kritik.
// Home eager (giriş sayfası ilk açıldığı için, blank screen olmasın)
import Home from "./pages/Home";
import GlobalMessenger from "./components/messenger/GlobalMessenger";
import GlobalAnnouncementStrip from "./components/announcements/GlobalAnnouncementStrip";
import DeviceRegistrar from "./components/DeviceRegistrar";
const OperatorFlow = lazy(() => import("./pages/OperatorFlow"));
const PlanFlow = lazy(() => import("./pages/PlanFlow"));
const ManagementFlow = lazy(() => import("./pages/ManagementFlow"));
const WarehouseFlow = lazy(() => import("./pages/WarehouseFlow"));
const PaintFlow = lazy(() => import("./pages/PaintFlow"));
const DriverFlow = lazy(() => import("./pages/DriverFlow"));
const LiveDashboard = lazy(() => import("./pages/LiveDashboard"));
const TrackingPage = lazy(() => import("./pages/TrackingPage"));
const BobinFlow = lazy(() => import("./pages/BobinFlow"));
const MarkaStokFlow = lazy(() => import("./pages/MarkaStokFlow"));
const BoyaciFlow = lazy(() => import("./pages/BoyaciFlow"));
const CameraFlow = lazy(() => import("./pages/CameraFlow"));
const PortalFlow = lazy(() => import("./pages/PortalFlow"));
const ShiftPlanFlow = lazy(() => import("./pages/ShiftPlanFlow"));
const TabletFlow = lazy(() => import("./pages/TabletFlow"));

// Suspense fallback — chunk indirilirken gösterilen yükleyici (industrial tema)
const RouteLoading = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 mx-auto mb-4 rounded-md bg-gradient-to-br from-primary to-amber-600 flex items-center justify-center text-black font-black text-xl shadow-gold-glow animate-pulse">
        B
      </div>
      <p className="text-[10px] font-mono uppercase tracking-widest text-text-secondary">
        Buse Kağıt
      </p>
      <div className="mt-3 w-32 h-0.5 mx-auto bg-border overflow-hidden rounded-full">
        <div className="h-full bg-primary animate-loading-bar" style={{
          animation: "loading-bar 1.2s ease-in-out infinite"
        }} />
      </div>
      <style>{`
        @keyframes loading-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        .animate-loading-bar { width: 25%; }
      `}</style>
    </div>
  </div>
);

export { API } from "./lib/api";

// Mobile/slow network detection — adaptive timeout
// Mobil veride RTT 200-800ms olabiliyor, 17+ paralel istekte cumulative gecikme yaşanır.
// Bu yüzden mobil cihazlarda timeout'u 2x'e çıkarıyoruz.
function detectSlowNetwork() {
  if (typeof navigator === "undefined") return false;
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const isSlowConn = conn && (
    conn.effectiveType === "slow-2g" ||
    conn.effectiveType === "2g" ||
    conn.effectiveType === "3g" ||
    conn.saveData === true
  );
  return isMobile || isSlowConn;
}

export const IS_SLOW_NETWORK = detectSlowNetwork();

// Global axios timeout - yavaş ağlarda infinite hanging'i önler
// Mobil/yavaş ağlarda 35s, masaüstü hızlı ağda 20s
axios.defaults.timeout = IS_SLOW_NETWORK ? 35000 : 20000;

// Axios interceptor - JWT token'ı her isteğe ekle + Idempotency-Key (POST/PUT/PATCH/DELETE)
// Idempotency-Key, çift-submit/network retry koruması için kullanılır. Backend middleware'i
// aynı key ile gelen istekleri 1 saat boyunca tek bir response ile döndürür.
const _genUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback (eski tarayıcılar)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Sadece mutasyon metodlarına Idempotency-Key ekle (GET'lere gerek yok)
  const method = (config.method || "").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    if (!config.headers["Idempotency-Key"] && !config.headers["idempotency-key"]) {
      config.headers["Idempotency-Key"] = _genUUID();
    }
  }
  return config;
});

// ─── JWT Refresh Token Yenileme ───
// Access token 30 dakika kısa ömürlüdür. 401 alındığında refresh token ile
// yeni bir access token almayı dener; başarısız olursa kullanıcıyı login'e atar.
// Yenileme fonksiyonunun kendisi lib/authRefresh.js'te — messenger.js'in WS
// yeniden-bağlanma mantığı da AYNI fonksiyonu kullanır (tek kaynak, bkz. o
// dosyadaki açıklama: önceden iki ayrı yenileme yolu birbirini geçersiz
// kılıyordu).

// 401 hatası alınca token temizle + Otomatik RETRY (mobile blokaj/timeout için)
axios.interceptors.response.use(
  (response) => {
    // Başarılı yanıt geldi → bağlantı durumu "online" olarak işaretle
    if (typeof window !== "undefined") {
      window.__apiOnline = true;
      window.dispatchEvent(new CustomEvent("api-online"));
    }
    // Login yanıtlarından refresh_token'ı otomatik kaydet (URL desenine bakar)
    try {
      const url = response?.config?.url || "";
      const data = response?.data;
      const isLoginish = /\/(login|auth\/refresh)$/.test(url) ||
                         /\/(users|management|dashboard|drivers)\/login$/.test(url);
      if (isLoginish && data && typeof data === "object") {
        if (data.token) localStorage.setItem("auth_token", data.token);
        if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
      }
    } catch (e) {
      // sessiz
    }
    return response;
  },
  async (error) => {
    const cfg = error.config || {};

    // 401 → önce refresh token ile yeni access almayı dene (tek seferlik)
    if (
      error.response?.status === 401 &&
      !cfg.url?.includes("/login") &&
      !cfg.url?.includes("/auth/refresh") &&
      !cfg.__skipAuthRefresh &&
      !cfg.__authRetried
    ) {
      const newToken = await tryRefreshAccessToken();
      if (newToken) {
        cfg.__authRetried = true;
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${newToken}`;
        return axios(cfg);
      }
      // Refresh başarısız. Token GERÇEKTEN reddedildiyse (401) authRefresh oturumu
      // zaten sildi. Geçici hatada (ağ, 5xx, 429) oturum korunur — bu istek başarısız
      // sayılır, sonraki istek yeniden dener. Sadece refresh token hiç yoksa temizle.
      if (!localStorage.getItem("refresh_token")) clearSession();
      return Promise.reject(error);
    }

    // Network hatası / timeout retry — mobil ISP blokajına karşı dayanıklılık
    const isNetworkError =
      !error.response ||
      error.code === "ECONNABORTED" ||
      error.code === "ERR_NETWORK" ||
      error.message === "Network Error";
    const isGetRequest = (cfg.method || "get").toLowerCase() === "get";
    const isLoginRequest = (cfg.url || "").includes("/login");
    cfg.__retryCount = cfg.__retryCount || 0;
    const MAX_RETRIES = isLoginRequest ? 1 : 3;

    if (isNetworkError && isGetRequest && cfg.__retryCount < MAX_RETRIES) {
      cfg.__retryCount += 1;
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.min(1000 * Math.pow(2, cfg.__retryCount - 1), 4000);

      // İlk retry'da kullanıcıya banner göster
      if (cfg.__retryCount === 1 && typeof window !== "undefined") {
        window.__apiOnline = false;
        window.dispatchEvent(new CustomEvent("api-offline"));
      }

      await new Promise((r) => setTimeout(r, delay));
      return axios(cfg);
    }

    // Tüm retry'lar tükendi → kullanıcıya bildir
    if (isNetworkError && typeof window !== "undefined") {
      window.__apiOnline = false;
      window.dispatchEvent(new CustomEvent("api-offline"));
    }

    return Promise.reject(error);
  }
);

// Firebase Service Worker kaydı - iOS Safari / ağ problemlerinde bloke etmemesi için
// Arka planda sessizce kaydet, hataları sessizce yok say
if ('serviceWorker' in navigator) {
  // index.html içinde zaten /service-worker.js kaydediliyor.
  // Firebase SW'yi sadece Notification API destekliyorsa ve iOS Safari değilse kaydet.
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const supportsPush = "PushManager" in window;
  if (!isIOS && supportsPush) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/firebase-messaging-sw.js')
        .catch(() => { /* sessiz */ });
    });
  }
}

// Ziyaretçi takip bileşeni
function VisitorTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Tablet hesabı /visitors/log'a erişemez (backend izin listesi, 403)
    if (isTabletSession()) return;
    const logVisit = async () => {
      try {
        await axios.post(`${API}/visitors/log`, {
          user_agent: navigator.userAgent,
          page_visited: location.pathname
        });
      } catch (error) {
        console.log("Visitor log error:", error);
      }
    };
    
    logVisit();
  }, [location.pathname]);
  
  return null;
}

// Ortak tablet ana sayfayı (modül kartları) hiç görmez: her zaman /tablet
function HomeRoute(props) {
  if (isSessionValid() && isTabletSession()) return <Navigate to="/tablet" replace />;
  return <Home {...props} />;
}

function App() {
  const [theme, setTheme] = useState("dark");
  const [liteMode, setLiteMode] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "dark";
    setTheme(savedTheme);
    const lite = localStorage.getItem("lite_mode") === "1";
    setLiteMode(lite);
    document.documentElement.className = savedTheme + (lite ? " lite-mode" : "");
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    document.documentElement.classList.add('theme-transitioning');
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.className = newTheme + ' theme-transitioning' + (liteMode ? ' lite-mode' : '');
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transitioning');
    }, 350);
  };

  const toggleLiteMode = () => {
    const next = !liteMode;
    setLiteMode(next);
    localStorage.setItem("lite_mode", next ? "1" : "0");
    document.documentElement.className = theme + (next ? " lite-mode" : "");
  };

  return (
    <CalendarThemeProvider>
    <ErrorBoundary>
      <ConfirmProvider>
        <div className={`App ${theme}`}>
          {/* WCAG AAA: Skip-to-content (klavye/ekran okuyucu) */}
          <a href="#main-content" className="skip-to-main">İçeriğe atla</a>
          <div className="ambient-layer" aria-hidden="true" />
          <ConnectionBanner />
          <BrowserRouter>
            <VisitorTracker />
            <GlobalAnnouncementStrip />
            <DeviceRegistrar />
            <Suspense fallback={<RouteLoading />}>
              <LoginArrival liteMode={liteMode}>
              <Routes>
                <Route path="/" element={<HomeRoute theme={theme} toggleTheme={toggleTheme} liteMode={liteMode} toggleLiteMode={toggleLiteMode} />} />
                <Route path="/operator" element={<ErrorBoundary><ProtectedRoute><OperatorFlow theme={theme} toggleTheme={toggleTheme} liteMode={liteMode} toggleLiteMode={toggleLiteMode} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/plan" element={<ErrorBoundary><ProtectedRoute><PlanFlow theme={theme} toggleTheme={toggleTheme} liteMode={liteMode} toggleLiteMode={toggleLiteMode} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/management" element={<ErrorBoundary><ProtectedRoute><ManagementFlow theme={theme} toggleTheme={toggleTheme} liteMode={liteMode} toggleLiteMode={toggleLiteMode} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/warehouse" element={<ErrorBoundary><ProtectedRoute><WarehouseFlow theme={theme} toggleTheme={toggleTheme} liteMode={liteMode} toggleLiteMode={toggleLiteMode} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/paint" element={<ErrorBoundary><ProtectedRoute><PaintFlow theme={theme} toggleTheme={toggleTheme} liteMode={liteMode} toggleLiteMode={toggleLiteMode} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/driver" element={<ErrorBoundary><ProtectedRoute><DriverFlow theme={theme} toggleTheme={toggleTheme} liteMode={liteMode} toggleLiteMode={toggleLiteMode} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/bobin" element={<ErrorBoundary><ProtectedRoute><BobinFlow theme={theme} toggleTheme={toggleTheme} liteMode={liteMode} toggleLiteMode={toggleLiteMode} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/marka-stok" element={<ErrorBoundary><ProtectedRoute><MarkaStokFlow theme={theme} toggleTheme={toggleTheme} liteMode={liteMode} toggleLiteMode={toggleLiteMode} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/boyaci" element={<ErrorBoundary><ProtectedRoute><BoyaciFlow theme={theme} toggleTheme={toggleTheme} liteMode={liteMode} toggleLiteMode={toggleLiteMode} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/kamera" element={<ErrorBoundary><ProtectedRoute><CameraFlow theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/vardiya-plani" element={<ErrorBoundary><ProtectedRoute><ShiftPlanFlow theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/tablet" element={<ErrorBoundary><ProtectedRoute><TabletFlow /></ProtectedRoute></ErrorBoundary>} />
                <Route path="/dashboard" element={<LiveDashboard />} />
                <Route path="/takip/:token" element={<TrackingPage theme={theme} liteMode={liteMode} />} />
                <Route path="/siparis-takip" element={<ErrorBoundary><PortalFlow theme={theme} /></ErrorBoundary>} />
              </Routes>
              </LoginArrival>
            </Suspense>
            {/* Global Messenger — tüm oturumlu sayfalarda görünür FAB */}
            <GlobalMessenger />
          </BrowserRouter>
          <Toaster position="top-center" richColors duration={2500} closeButton swipeDirections={["right", "left"]} />
        </div>
      </ConfirmProvider>
    </ErrorBoundary>
    </CalendarThemeProvider>
  );
}

export default App;
